import React, { useEffect, useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import { getTasks, createTask, updateTask, deleteTask, getUsers, getEvents } from '../services/api';

interface TasksTabProps {
  entityType: 'student' | 'prospect' | 'lead';
  entityId: string;
  documents?: any[];
}

const priorityColors: Record<string, string> = {
  urgent: '#ffebee',
  high: '#fff3e0',
  normal: '#ffffff',
  low: '#f5f5f5',
};

const priorityBadgeColors: Record<string, string> = {
  urgent: 'badge-red',
  high: 'badge-yellow',
  normal: 'badge-blue',
  low: 'badge-gray',
};

const statusBadgeColors: Record<string, string> = {
  new: 'badge-blue',
  in_progress: 'badge-gold',
  completed: 'badge-green',
  cancelled: 'badge-gray',
};

const priorityLabels: Record<string, string> = {
  low: 'נמוכה',
  normal: 'רגילה',
  high: 'גבוהה',
  urgent: 'דחופה',
};

const statusLabels: Record<string, string> = {
  new: 'חדשה',
  in_progress: 'בטיפול',
  completed: 'הושלמה',
  cancelled: 'בוטלה',
};

const taskTypeLabels: Record<string, string> = {
  standalone: 'משימה עצמאית',
  event_based: 'מבוססת אירוע',
  document_based: 'מבוססת מסמך',
};

export default function TasksTab({ entityType, entityId, documents = [] }: TasksTabProps) {
  const { t } = useLang();
  const { userName } = useUser();
  const [tasks, setTasks] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [form, setForm] = useState({
    title: '', description: '', taskType: 'standalone', relatedEvent: '', relatedDocument: '',
    dueDate: '', priority: 'normal', assignedTo: '', ccTo: '', status: 'new', notes: ''
  });
  const [sendForm, setSendForm] = useState({
    title: '', description: '', dueDate: '', assignedTo: ''
  });

  const loadTasks = () => {
    getTasks({ relatedEntityId: entityId }).then(res => setTasks(res.data)).catch(() => {});
  };

  useEffect(() => {
    loadTasks();
    getUsers().then(res => setUsers(res.data)).catch(() => {});
    getEvents().then(res => setEvents(res.data)).catch(() => {});
  }, [entityId]);

  const resetForm = () => {
    setForm({
      title: '', description: '', taskType: 'standalone', relatedEvent: '', relatedDocument: '',
      dueDate: '', priority: 'normal', assignedTo: '', ccTo: '', status: 'new', notes: ''
    });
    setEditingTask(null);
  };

  const handleSave = async () => {
    const taskData = {
      ...form,
      relatedEntityType: entityType,
      relatedEntityId: entityId,
      createdBy: userName,
    };
    if (editingTask) {
      await updateTask(editingTask.id, taskData);
    } else {
      await createTask(taskData);
    }
    setShowModal(false);
    resetForm();
    loadTasks();
  };

  const handleEdit = (task: any) => {
    setForm({
      title: task.title || '',
      description: task.description || '',
      taskType: task.taskType || 'standalone',
      relatedEvent: task.relatedEvent || '',
      relatedDocument: task.relatedDocument || '',
      dueDate: task.dueDate || '',
      priority: task.priority || 'normal',
      assignedTo: task.assignedTo || '',
      ccTo: task.ccTo || '',
      status: task.status || 'new',
      notes: task.notes || '',
    });
    setEditingTask(task);
    setShowModal(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק משימה זו?')) {
      await deleteTask(id);
      loadTasks();
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    await updateTask(id, { status: newStatus });
    loadTasks();
  };

  const handleSendTask = async () => {
    const taskData = {
      ...sendForm,
      taskType: 'standalone',
      priority: 'normal',
      status: 'new',
      relatedEntityType: entityType,
      relatedEntityId: entityId,
      createdBy: userName,
      ccTo: '',
      relatedEvent: '',
      relatedDocument: '',
      notes: '',
    };
    await createTask(taskData);
    setShowSendModal(false);
    setSendForm({ title: '', description: '', dueDate: '', assignedTo: '' });
    loadTasks();
  };

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user ? user.name : userId || '-';
  };

  const filteredTasks = filterStatus === 'all' ? tasks : tasks.filter(t => t.status === filterStatus);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          + משימה חדשה
        </button>
        <button className="btn btn-secondary" onClick={() => setShowSendModal(true)}>
          📤 שלח משימה
        </button>
        <div style={{ marginRight: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: '#666' }}>סינון:</label>
          <select className="form-select" style={{ width: 'auto', minWidth: 120 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="all">הכל</option>
            <option value="new">חדשה</option>
            <option value="in_progress">בטיפול</option>
            <option value="completed">הושלמה</option>
            <option value="cancelled">בוטלה</option>
          </select>
        </div>
      </div>

      <div className="table-wrapper">
      <table className="data-table">
        <thead>
          <tr>
            <th>כותרת</th>
            <th>סוג</th>
            <th>מבצע</th>
            <th>תאריך יעד</th>
            <th>עדיפות</th>
            <th>סטטוס</th>
            <th>פעולות</th>
          </tr>
        </thead>
        <tbody>
          {filteredTasks.map(task => (
            <tr key={task.id} style={{ background: priorityColors[task.priority] || '#fff' }}>
              <td>
                <strong>{task.title}</strong>
                {task.description && <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{task.description.substring(0, 50)}{task.description.length > 50 ? '...' : ''}</div>}
              </td>
              <td>{taskTypeLabels[task.taskType] || task.taskType}</td>
              <td>{getUserName(task.assignedTo)}</td>
              <td dir="ltr">{task.dueDate || '-'}</td>
              <td><span className={`badge ${priorityBadgeColors[task.priority]}`}>{priorityLabels[task.priority]}</span></td>
              <td>
                <select
                  className="form-select"
                  style={{ width: 'auto', minWidth: 90, padding: '2px 6px', fontSize: 12 }}
                  value={task.status}
                  onChange={e => handleStatusChange(task.id, e.target.value)}
                >
                  <option value="new">חדשה</option>
                  <option value="in_progress">בטיפול</option>
                  <option value="completed">הושלמה</option>
                  <option value="cancelled">בוטלה</option>
                </select>
              </td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-sm btn-outline" onClick={() => handleEdit(task)}>עריכה</button>
                  <button className="btn btn-sm btn-danger" onClick={() => handleDelete(task.id)}>מחיקה</button>
                </div>
              </td>
            </tr>
          ))}
          {filteredTasks.length === 0 && (
            <tr><td colSpan={7} style={{ textAlign: 'center', color: '#999' }}>{t('noData')}</td></tr>
          )}
        </tbody>
      </table>
      </div>

      {/* Create/Edit Task Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600 }}>
            <div className="modal-header">
              <h3 className="modal-title">{editingTask ? 'עריכת משימה' : 'משימה חדשה'}</h3>
              <button className="modal-close" onClick={() => { setShowModal(false); resetForm(); }}>×</button>
            </div>
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label className="form-label">כותרת המשימה *</label>
                <input className="form-input" value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">תיאור</label>
                <textarea className="form-textarea" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">סוג משימה</label>
                  <select className="form-select" value={form.taskType} onChange={e => setForm({...form, taskType: e.target.value, relatedEvent: '', relatedDocument: ''})}>
                    <option value="standalone">משימה עצמאית</option>
                    <option value="event_based">מבוססת אירוע</option>
                    <option value="document_based">מבוססת מסמך</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">עדיפות</label>
                  <select className="form-select" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})}>
                    <option value="low">נמוכה</option>
                    <option value="normal">רגילה</option>
                    <option value="high">גבוהה</option>
                    <option value="urgent">דחופה</option>
                  </select>
                </div>
              </div>

              {form.taskType === 'event_based' && (
                <div className="form-group">
                  <label className="form-label">אירוע מקושר</label>
                  <select className="form-select" value={form.relatedEvent} onChange={e => setForm({...form, relatedEvent: e.target.value})}>
                    <option value="">-- בחר אירוע --</option>
                    {events.map(evt => (
                      <option key={evt.id} value={evt.id}>{evt.title} ({evt.date})</option>
                    ))}
                  </select>
                </div>
              )}

              {form.taskType === 'document_based' && (
                <div className="form-group">
                  <label className="form-label">מסמך מקושר</label>
                  <select className="form-select" value={form.relatedDocument} onChange={e => setForm({...form, relatedDocument: e.target.value})}>
                    <option value="">-- בחר מסמך --</option>
                    {documents.map(doc => (
                      <option key={doc.id} value={doc.fileName}>{doc.fileName} ({doc.docType})</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">תאריך יעד</label>
                  <input className="form-input" type="date" value={form.dueDate} onChange={e => setForm({...form, dueDate: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">סטטוס</label>
                  <select className="form-select" value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                    <option value="new">חדשה</option>
                    <option value="in_progress">בטיפול</option>
                    <option value="completed">הושלמה</option>
                    <option value="cancelled">בוטלה</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">מבצע המשימה</label>
                  <select className="form-select" value={form.assignedTo} onChange={e => setForm({...form, assignedTo: e.target.value})}>
                    <option value="">-- בחר מבצע --</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">לידיעה</label>
                  <select className="form-select" value={form.ccTo} onChange={e => setForm({...form, ccTo: e.target.value})}>
                    <option value="">-- ללא --</option>
                    {users.map(u => (
                      <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">נוצר על ידי</label>
                <div className="form-input" style={{ background: '#f8f9fa' }}>{userName}</div>
              </div>
              <div className="form-group">
                <label className="form-label">הערות</label>
                <textarea className="form-textarea" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.title}>{editingTask ? 'עדכן' : 'צור משימה'}</button>
              <button className="btn btn-outline" onClick={() => { setShowModal(false); resetForm(); }}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Send Task Modal */}
      {showSendModal && (
        <div className="modal-overlay" onClick={() => setShowSendModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">📤 שלח משימה</h3>
              <button className="modal-close" onClick={() => setShowSendModal(false)}>×</button>
            </div>
            <div className="form-group">
              <label className="form-label">שלח אל *</label>
              <select className="form-select" value={sendForm.assignedTo} onChange={e => setSendForm({...sendForm, assignedTo: e.target.value})}>
                <option value="">-- בחר משתמש --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">כותרת *</label>
              <input className="form-input" value={sendForm.title} onChange={e => setSendForm({...sendForm, title: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">תיאור</label>
              <textarea className="form-textarea" value={sendForm.description} onChange={e => setSendForm({...sendForm, description: e.target.value})} />
            </div>
            <div className="form-group">
              <label className="form-label">תאריך יעד</label>
              <input className="form-input" type="date" value={sendForm.dueDate} onChange={e => setSendForm({...sendForm, dueDate: e.target.value})} />
            </div>
            <div className="modal-actions">
              <button className="btn btn-primary" onClick={handleSendTask} disabled={!sendForm.title || !sendForm.assignedTo}>📤 שלח</button>
              <button className="btn btn-outline" onClick={() => setShowSendModal(false)}>{t('cancel')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
